// src/pages/EditProductPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type CodeRow = {
  product_code: string;
  product_name: string;
};

type Product = {
  id: number;
  code: string;
  name: string;
  price: number;
  wholesalePrice: number;
  mallPrice: number;  
  stockOnHand: number | null;
  unit?: string;
  createdAt: string;
  imageUrl?: string;
  groupTypeId: number | null;
};

type PatchBody = Partial<{
  product_name: string;
  retail_price: number;
  wholesale_price: number;
  mall_price: number;  
  stock_on_hand: number | null;
  unit: string | null;
  url_images: string | null;
  groups_type_id: number | null;
}>;

type GroupRow = { id: number; group_name: string };

const thb = (n: number) => n.toLocaleString("th-TH", { style: "currency", currency: "THB" });

export default function EditProductPage(): JSX.Element {
  // ซ้าย: รายการรหัส
  const [codes, setCodes] = useState<CodeRow[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codesErr, setCodesErr] = useState("");

  // ค้นหาซ้าย
  const [q, setQ] = useState("");

  // ขวา: ฟอร์มแก้ไข
  const [selectedCode, setSelectedCode] = useState<string>("");
  const [product, setProduct] = useState<Product | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [productErr, setProductErr] = useState("");

  // draft ฟอร์ม
  const [name, setName] = useState("");
  const [retailPrice, setRetailPrice] = useState<string>("");     // string สำหรับ input
  const [wholesalePrice, setWholesalePrice] = useState<string>("");
  const [mallPrice, setMallPrice] = useState<string>(""); 
  const [stockOnHand, setStockOnHand] = useState<string>("");
  const [unit, setUnit] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");

  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupId, setGroupId] = useState<string>(""); // เก็บเป็นสตริงของ id หรือ "" (ไม่ระบุ)

  // สถานะบันทึก
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [toast, setToast] = useState<{show: boolean; message: string; tone?: "success" | "warn" | "error"}>({
    show: false, message: ""
  });

  // โหลดรายการรหัสทั้งหมด
  const loadCodes = async () => {
    setLoadingCodes(true);
    setCodesErr("");
    try {
      const resp = await fetch(apiUrl("/products-list-codes"));
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
      const items: CodeRow[] = Array.isArray(j?.items) ? j.items : [];
      setCodes(items);
    } catch (e: any) {
      setCodesErr(e?.message ?? "โหลดรหัสสินค้าไม่สำเร็จ");
    } finally {
      setLoadingCodes(false);
    }
  };

  useEffect(() => {
    void loadCodes();
  }, []);

  useEffect(() => {
      (async () => {
        try {
          setLoadingGroups(true);
          const resp = await fetch(apiUrl("/groups_list"));
          const j = await resp.json().catch(() => ({}));
          const items: GroupRow[] = Array.isArray(j?.items) ? j.items : [];
          setGroups(items);
       } finally {
          setLoadingGroups(false);
        }
      })();
  }, []);

  // เลือกแล้วโหลดรายละเอียดสินค้า
  const loadProduct = async (code: string) => {
    if (!code) return;
    setProduct(null);
    setLoadingProduct(true);
    setProductErr("");
    try {
      const resp = await fetch(apiUrl(`/products/${encodeURIComponent(code)}`));
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
      const p: Product = j?.item;
      setProduct(p);

      // sync ฟอร์ม
      setName(p.name ?? "");
      setRetailPrice(Number.isFinite(p.price) ? String(p.price) : "");
      setWholesalePrice(Number.isFinite(p.wholesalePrice) ? String(p.wholesalePrice) : "");
      setMallPrice(Number.isFinite(p.mallPrice) ? String(p.mallPrice) : ""); 
      setStockOnHand(p.stockOnHand == null ? "" : String(p.stockOnHand));
      setUnit(p.unit ?? "");
      setImageUrl(p.imageUrl ?? "");
      setGroupId(p.groupTypeId == null ? "" : String(p.groupTypeId));
    } catch (e: any) {
      setProductErr(e?.message ?? "โหลดรายละเอียดสินค้าไม่สำเร็จ");
    } finally {
      setLoadingProduct(false);
    }
  };

  // เมื่อเลือกโค้ดใหม่
  useEffect(() => {
    if (selectedCode) void loadProduct(selectedCode);
  }, [selectedCode]);

  // กรองด้านซ้าย
  const filteredCodes = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return codes;
    return codes.filter(
      (r) =>
        r.product_code.toLowerCase().includes(qq) ||
        (r.product_name ?? "").toLowerCase().includes(qq)
    );
  }, [codes, q]);

  // helper: แสดง toast สั้น ๆ
  const showToast = (message: string, tone: "success" | "warn" | "error" = "success") => {
    setToast({ show: true, message, tone });
    setTimeout(() => setToast({ show: false, message: "" }), 1800);
  };

  // ตรวจว่าเปลี่ยนจริงไหม
  const hasChanges = (): boolean => {
    if (!product) return false;
    const toNum = (s: string) => (s.trim() === "" ? NaN : Number(s));
    return (
      (name ?? "") !== (product.name ?? "") ||
      toNum(retailPrice) !== product.price ||
      toNum(wholesalePrice) !== product.wholesalePrice ||
      toNum(mallPrice) !== product.mallPrice ||    
      (stockOnHand.trim() === "" ? null : Number(stockOnHand)) !== (product.stockOnHand ?? null) ||
      (unit ?? "") !== (product.unit ?? "") ||
      (imageUrl ?? "") !== (product.imageUrl ?? "") ||
      ((groupId.trim() === "" ? null : Number(groupId)) !== (product.groupTypeId ?? null))
    );
  };

  // PMN-CRITICAL(/EditProductPage): patch keys map directly to products DB columns used by sale pages; keep price fields aligned.
  // แปลง draft → body สำหรับ PATCH
  const makePatch = (): PatchBody => {
    const body: PatchBody = {};
    const numOr = (s: string, fallback: number | null) =>
      s.trim() === "" ? fallback : Number(s);

    // ค่าที่อนุญาตให้แก้
    body.product_name = name;
    body.retail_price = numOr(retailPrice, NaN) as number;
    body.wholesale_price = numOr(wholesalePrice, NaN) as number;
    body.mall_price = numOr(mallPrice, NaN) as number; 
    body.stock_on_hand = stockOnHand.trim() === "" ? null : Number(stockOnHand);
    body.unit = unit.trim() === "" ? null : unit;
    body.url_images = imageUrl.trim() === "" ? null : imageUrl;
    body.groups_type_id = groupId.trim() === "" ? null : Number(groupId);

    return body;
  };

  // validate คร่าว ๆ
  const validate = (): string | null => {
    const toNum = (s: string) => (s.trim() === "" ? NaN : Number(s));
    if (!name.trim()) return "กรุณากรอกชื่อสินค้า";
    if (!Number.isFinite(toNum(retailPrice)) || toNum(retailPrice) < 0) return "ราคาขายต้องเป็นตัวเลข ≥ 0";
    if (!Number.isFinite(toNum(wholesalePrice)) || toNum(wholesalePrice) < 0) return "ราคาส่งต้องเป็นตัวเลข ≥ 0";
    if (!Number.isFinite(toNum(mallPrice)) || toNum(mallPrice) < 0)
    return "ราคาขายบนห้างต้องเป็นตัวเลข ≥ 0";  
    if (stockOnHand.trim() !== "" && (!Number.isFinite(Number(stockOnHand)) || Number(stockOnHand) < 0)) {
      return "สต็อกคงเหลือต้องเป็นตัวเลข ≥ 0 (หรือปล่อยว่าง)";
    }
    return null;
    };

  const save = async () => {
    if (!product) return;
    setSaveErr("");

    const err = validate();
    if (err) {
      setSaveErr(err);
      showToast(err, "warn");
      return;
    }

    // PMN-CRITICAL(/EditProductPage): saving product master changes future sale prices/names/images; product_code itself must remain immutable here.
    const body = makePatch();
    // ลบฟิลด์ที่ไม่เปลี่ยนเพื่อลด payload
    if (body.product_name === product.name) delete body.product_name;
    if (Number(body.retail_price) === product.price) delete body.retail_price;
    if (Number(body.wholesale_price) === product.wholesalePrice) delete body.wholesale_price;
    if (Number(body.mall_price) === product.mallPrice) delete body.mall_price;
    if ((body.stock_on_hand ?? null) === (product.stockOnHand ?? null)) delete body.stock_on_hand;
    if ((body.unit ?? "") === (product.unit ?? "")) delete body.unit;
    if ((body.url_images ?? "") === (product.imageUrl ?? "")) delete body.url_images;
    if ((body.groups_type_id ?? null) === (product.groupTypeId ?? null)) delete body.groups_type_id;

    if (Object.keys(body).length === 0) {
      showToast("ไม่มีการเปลี่ยนแปลง", "warn");
      return;
    }

    setSaving(true);
    try {
      const resp = await fetch(apiUrl(`/products/${encodeURIComponent(product.code)}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || j?.ok !== true) throw new Error(j?.error ?? resp.statusText);

      showToast("บันทึกแล้ว ✅", "success");

      // reload รายการขวา + ซ้าย (เพื่อให้ชื่อที่อาจเปลี่ยน สะท้อนใน list)
      await loadProduct(product.code);
      await loadCodes();
    } catch (e: any) {
      const msg = e?.message ?? "บันทึกไม่สำเร็จ";
      setSaveErr(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div
            className={`alert ${
              toast.tone === "error" ? "alert-danger" : toast.tone === "warn" ? "alert-warning" : "alert-success"
            } shadow mb-0 rounded-3 py-2 px-3`}
          >
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-11">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">แก้ไขสินค้า (Edit Product)</h4>

                <div className="row g-4">
                  {/* ซ้าย: รายการโค้ด */}
                  <div className="col-12 col-lg-5 col-xxl-4">
                    <div className="card border-0 shadow-sm rounded-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="fw-semibold">รายการรหัสสินค้า</div>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => void loadCodes()}
                              disabled={loadingCodes}
                            >
                              {loadingCodes ? "กำลังโหลด…" : "รีเฟรช"}
                            </button>
                            <a href="/" className="btn btn-link btn-sm text-decoration-none">← กลับเมนู</a>
                          </div>
                        </div>

                        <div className="input-group mb-3">
                          <span className="input-group-text">ค้นหา</span>
                          <input
                            className="form-control"
                            placeholder="ค้นหาจากรหัสหรือชื่อ"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                          />
                        </div>

                        {codesErr && <div className="alert alert-warning py-2">{codesErr}</div>}

                        <div
                        className="list-group"
                        style={{
                            maxHeight: 520,
                            overflowY: "auto",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                        }}
                        >
                        {filteredCodes.map((r) => {
                            const active = r.product_code === selectedCode;
                            return (
                              <button
                                key={r.product_code}
                                type="button"
                                className={`list-group-item list-group-item-action text-start ${active ? "active" : ""}`}
                                onClick={() => setSelectedCode(r.product_code)}
                                title={r.product_name}
                                style={{
                                  // ทำให้ความกว้างรายการยาวกว่า container เพื่อให้เกิดสกรอลล์แนวนอน
                                  minWidth: "650px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span className={`fw-semibold me-2 ${active ? "text-white" : ""}`}>
                                  {r.product_code}
                                </span>
                                <span
                                  className={active ? "text-white-50" : "text-muted"}
                                  style={{
                                    display: "inline-block",
                                    verticalAlign: "middle",
                                    maxWidth: "calc(100% - 90px)", // เหลือพื้นที่ให้รหัส
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {r.product_name}
                                </span>
                              </button>
                            );
                        })}
                        </div>


                        <div className="small text-muted mt-2">
                          ทั้งหมด {codes.length.toLocaleString()} รายการ
                          {q && <> • หลังกรอง {filteredCodes.length.toLocaleString()}</>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ขวา: ฟอร์มแก้ไข */}
                  <div className="col-12 col-lg-7 col-xxl-8">
                    <div className="card border-0 shadow-sm rounded-3">
                      <div className="card-body">
                        {!selectedCode && <div className="text-muted">เลือกสินค้าจากด้านซ้ายเพื่อแก้ไข</div>}

                        {selectedCode && (
                          <>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <div className="small text-muted">รหัสสินค้า</div>
                                <div className="fs-4 fw-bold">{selectedCode}</div>
                              </div>
                              <div className="text-end small text-muted">
                                {product && (
                                  <>
                                    <div>สร้างเมื่อ: {new Date(product.createdAt).toLocaleString("th-TH")}</div>
                                    <div>ราคาขายปัจจุบัน: {thb(product.price)}</div>
                                  </>
                                )}
                              </div>
                            </div>

                            {productErr && <div className="alert alert-warning py-2">{productErr}</div>}
                            {loadingProduct && (
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="spinner-border spinner-border-sm" /> <span>กำลังโหลดข้อมูลสินค้า…</span>
                              </div>
                            )}

                            {product && (
                              <form
                                className="row g-3"
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  void save();
                                }}
                              >
                                <div className="col-12">
                                  <label className="form-label">ชื่อสินค้า</label>
                                  <input
                                    className="form-control"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                  />
                                </div>
                                <div className="col-12 col-md-6">

                                  <label className="form-label me-2">กลุ่มสินค้า (groups_type_id)</label>
                                  <a
                                    href="/AddGroupPage"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                    >
                                    + เพิ่มกลุ่มสินค้าใหม่
                                  </a>
                                  <select
                                    className="form-select"
                                    value={groupId}
                                    onChange={(e) => setGroupId(e.target.value)}
                                    disabled={loadingGroups}
                                  >
                                    <option value="">— ไม่ระบุ —</option>
                                    {groups.map(g => (
                                      <option key={g.id} value={g.id}>{g.group_name}</option>
                                    ))}
                                  </select>
                                </div>

                                <div className="col-12 col-md-6">
                                  <label className="form-label">ราคาขาย (retail_price)</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="form-control"
                                    value={retailPrice}
                                    onChange={(e) => setRetailPrice(e.target.value)}
                                  />
                                </div>

                                <div className="col-12 col-md-6">
                                  <label className="form-label">ราคาส่ง (wholesale_price)</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="form-control"
                                    value={wholesalePrice}
                                    onChange={(e) => setWholesalePrice(e.target.value)}
                                  />
                                </div>

                                <div className="col-12 col-md-6">
                                  <label className="form-label">ราคาขายบนห้าง (mall_price)</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="form-control"
                                    value={mallPrice}
                                    onChange={(e) => setMallPrice(e.target.value)}
                                  />
                                </div>

                                <div className="col-12">
                                  <label className="form-label">ลิงก์รูปสินค้า (url_images)</label>
                                  <input
                                    className="form-control"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder="https://…"
                                    readOnly
                                  />
                                  {imageUrl && (
                                    <div className="mt-2">
                                      <img
                                        src={imageUrl}
                                        alt="preview"
                                        style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = "none";
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="d-flex justify-content-between align-items-center mt-2">
                                  <div className="small text-muted">
                                    {hasChanges()
                                      ? "มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก"
                                      : "ไม่มีการเปลี่ยนแปลง"}
                                  </div>
                                  <div className="d-flex gap-2">
                                    {saveErr && (
                                      <span className="text-danger small align-self-center">{saveErr}</span>
                                    )}
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => product && void loadProduct(product.code)}
                                      disabled={loadingProduct || saving}
                                      title="โหลดค่าจากฐานข้อมูลใหม่"
                                    >
                                      รีเซ็ต/รีโหลด
                                    </button>
                                    <button
                                      type="submit"
                                      className="btn btn-primary"
                                      disabled={saving || !hasChanges()}
                                    >
                                      {saving ? "กำลังบันทึก…" : "บันทึกการแก้ไข"}
                                    </button>
                                  </div>
                                </div>
                              </form>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * ใช้เส้น API: <code>/products/list-codes</code>, <code>/products/:code [GET]</code>, <code>/products/:code [PATCH]</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
